/**
 * KLineCharts custom indicators for Macro Overlay.
 *
 * Each pinned macro series gets its own dynamically-registered indicator with
 * the name `MACRO_<series_id>`. Data is injected via window.__macro_data_<series_id>
 * before the indicator is created/overridden so that calc() has data on first draw.
 *
 * Usage:
 *   1. Set `(window as MacroWindow).__macro_data_LK_OPR = [{date, value}...]`
 *   2. Call `registerMacroIndicator('LK_OPR', '#f59e0b', 'OPR', '%', 'Monthly')`
 *   3. `chart.createIndicator('MACRO_LK_OPR', false, { id: 'macro_pane_LK_OPR', height: 80, minHeight: 50 })`
 */

import { registerIndicator, IndicatorSeries, LineType, type IndicatorTemplate, type IndicatorDrawParams } from 'klinecharts';

export const MACRO_IND_PREFIX  = 'MACRO_';
export const MACRO_PANE_PREFIX = 'macro_pane_';

type MacroPoint = { date: string; value: number; unit?: string };
type KBarBasic  = { timestamp: number };

/** Build the window key for a series */
export function macroWindowKey(seriesId: string): string {
  return `__macro_data_${seriesId}`;
}

/** Indicator name for a series */
export function macroIndName(seriesId: string): string {
  return MACRO_IND_PREFIX + seriesId;
}

/** Pane ID for a series */
export function macroPaneId(seriesId: string): string {
  return MACRO_PANE_PREFIX + seriesId;
}

/** Determine moving-average period(s) from a frequency string */
export function getMAPeriods(frequency: string | undefined): number[] {
  const freq = frequency?.toLowerCase().trim() ?? '';
  if (freq === 'monthly') return [12];
  if (freq === 'quarterly') return [4];
  if (freq === 'daily') return [20, 10];
  return [];
}

/** Shift a hex colour's hue by the given degrees (-180…180) */
function shiftHexHue(hex: string, deg: number): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h = (h / 6 + deg / 360 + 1) % 1;
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const nr = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const ng = Math.round(hue2rgb(p, q, h) * 255);
  const nb = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
}

/** Return per-MA colours derived from the base colour */
export function getMAColors(baseColor: string, frequency: string | undefined): string[] {
  const periods = getMAPeriods(frequency);
  if (periods.length === 0) return [];
  if (periods.length === 1) return [shiftHexHue(baseColor, 30)];
  return [shiftHexHue(baseColor, 30), shiftHexHue(baseColor, -30)];
}

/** Simple moving average with undefined gaps */
function computeSMA(values: (number | undefined)[], period: number): (number | undefined)[] {
  const result: (number | undefined)[] = [];
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) {
      result.push(undefined);
      continue;
    }
    let sum = 0;
    let count = 0;
    for (let j = 0; j < period; j++) {
      const v = values[i - j];
      if (v !== undefined && !isNaN(v)) {
        sum += v;
        count++;
      }
    }
    result.push(count > 0 ? sum / count : undefined);
  }
  return result;
}

/** Linear-interpolate between macro data points.
 *  Returns undefined for bars before the first point or after the last point
 *  so that neither the raw line nor MAs extrapolate outside the data range. */
function interpolateLinear(points: MacroPoint[], barDateStr: string): number | undefined {
  if (points.length === 0) return undefined;

  const barTime = new Date(barDateStr).getTime();

  let before: MacroPoint | undefined;
  let after: MacroPoint | undefined;

  for (const p of points) {
    const pTime = new Date(p.date).getTime();
    if (pTime <= barTime) {
      before = p;
    } else {
      after = p;
      break;
    }
  }

  if (!before) return undefined; // before first data point
  if (!after) {
    // at or after last data point — only return exact match
    const beforeTime = new Date(before.date).getTime();
    return barTime === beforeTime ? before.value : undefined;
  }

  const beforeTime = new Date(before.date).getTime();
  const afterTime = new Date(after.date).getTime();

  if (afterTime === beforeTime) return before.value;

  const t = (barTime - beforeTime) / (afterTime - beforeTime);
  return before.value + t * (after.value - before.value);
}

/** Build the figure definitions for a macro indicator (raw + optional MAs) */
export function buildMacroFigures(
  shortName: string,
  unit: string | null | undefined,
  frequency: string | undefined,
): Array<{ key: string; title: string; type: string }> {
  const figureTitle = shortName + (unit ? ` (${unit})` : '') + ' ';
  const figures: Array<{ key: string; title: string; type: string }> = [
    { key: 'value', title: figureTitle, type: 'line' },
  ];
  for (const period of getMAPeriods(frequency)) {
    figures.push({ key: `ma${period}`, title: `${period}-pt MA `, type: 'line' });
  }
  return figures;
}

/** Build the line-style definitions matching the figures array.
 *  size:0 suppresses KLineCharts default rendering so we can draw manually. */
export function buildMacroLineStyles(
  color: string,
  frequency: string | undefined,
): Array<{ color: string; size: number; style: LineType; smooth: boolean; dashedValue: number[] }> {
  const maColors = getMAColors(color, frequency);
  const lines: Array<{ color: string; size: number; style: LineType; smooth: boolean; dashedValue: number[] }> = [
    { color, size: 0, style: LineType.Solid, smooth: false, dashedValue: [2, 2] },
  ];
  for (const maColor of maColors) {
    lines.push({ color: maColor, size: 0, style: LineType.Solid, smooth: false, dashedValue: [4, 4] });
  }
  return lines;
}

export function createMacroCalc(seriesId: string, frequency?: string) {
  const windowKey = macroWindowKey(seriesId);
  const maPeriods = getMAPeriods(frequency);
  return (dataList: KBarBasic[]): Array<Record<string, number | undefined>> => {
    const rawPoints: MacroPoint[] = (window as unknown as Record<string, unknown>)[windowKey] as MacroPoint[] ?? [];
    const values = dataList.map((bar) => {
      const d = new Date(bar.timestamp);
      const barDate = d.toISOString().slice(0, 10);
      return interpolateLinear(rawPoints, barDate);
    });

    const result: Array<Record<string, number | undefined>> = values.map((value) => ({ value }));

    for (const period of maPeriods) {
      const ma = computeSMA(values, period);
      const key = `ma${period}`;
      for (let i = 0; i < result.length; i++) {
        result[i][key] = ma[i];
      }
    }

    return result;
  };
}

/**
 * Registers a KLineCharts indicator for a macro series.
 * Safe to call multiple times — catches the "already registered" error.
 */
export function registerMacroIndicator(
  seriesId: string,
  color: string,
  shortName: string,
  unit?: string | null,
  frequency?: string,
): void {
  const name = macroIndName(seriesId);
  const figureTitle = shortName + (unit ? ` (${unit})` : '') + ' ';

  const maPeriods = getMAPeriods(frequency);
  const maColors = getMAColors(color, frequency);

  // key→colour map so draw() can look up the right colour for each MA line
  const maColorMap: Record<string, string> = {};
  for (let i = 0; i < maPeriods.length; i++) {
    maColorMap[`ma${maPeriods[i]}`] = maColors[i];
  }

  const figures: Array<{ key: string; title: string; type: string }> = [
    { key: 'value', title: figureTitle, type: 'line' },
  ];
  const lines: Array<{ color: string; size: number; style: LineType; smooth: boolean; dashedValue: number[] }> = [
    { color, size: 0, style: LineType.Solid, smooth: false, dashedValue: [2, 2] },
  ];

  for (let i = 0; i < maPeriods.length; i++) {
    figures.push({ key: `ma${maPeriods[i]}`, title: `${maPeriods[i]}-pt MA `, type: 'line' });
    lines.push({ color: maColors[i], size: 0, style: LineType.Solid, smooth: false, dashedValue: [4, 4] });
  }

  type Result = Record<string, number | undefined>;

  const template: IndicatorTemplate<Result> = {
    name,
    shortName,
    series: IndicatorSeries.Normal,
    shouldOhlc: false,
    shouldFormatBigNumber: true,
    calcParams: [0],
    figures,
    styles: { lines },
    calc: createMacroCalc(seriesId, frequency),
    draw: (params: IndicatorDrawParams<Result>) => {
      const { ctx, indicator, bounding, xAxis, yAxis, visibleRange, kLineDataList } = params;
      const result = indicator.result;
      const { from, to } = visibleRange;
      if (to <= from || result.length === 0) return true;

      // Discover MA keys dynamically from the result data (handles stale templates)
      const sample = result.find((r) => r && Object.keys(r).length > 1);
      const maKeys = sample ? Object.keys(sample).filter((k) => k !== 'value') : [];

      // Build straight-line segments between actual macro data points.
      // calc() interpolates in *time* space, but the chart x-axis is *bar index*
      // space.  Weekends/holidays make those two spaces non-linear, so a time-
      // interpolation looks curved / stepped on the chart.  We fix this by
      // drawing direct bar-index-space lines between the macro points.
      const rawPoints: MacroPoint[] = (window as unknown as Record<string, unknown>)[macroWindowKey(seriesId)] as MacroPoint[] ?? [];

      // Fast O(n) date→index map instead of O(n×m) findIndex
      const barDateToIndex = new Map<string, number>();
      for (let i = 0; i < kLineDataList.length; i++) {
        const barDate = new Date(kLineDataList[i].timestamp).toISOString().slice(0, 10);
        barDateToIndex.set(barDate, i);
      }

      const macroPointIndices: { index: number; value: number }[] = [];
      for (const p of rawPoints) {
        const idx = barDateToIndex.get(p.date);
        if (idx !== undefined) {
          macroPointIndices.push({ index: idx, value: p.value });
        }
      }
      macroPointIndices.sort((a, b) => a.index - b.index);

      ctx.save();

      // ── Raw value line: straight segments between macro points ──────────────
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      for (let j = 0; j < macroPointIndices.length - 1; j++) {
        const p1 = macroPointIndices[j];
        const p2 = macroPointIndices[j + 1];
        const x1 = xAxis.convertToPixel(p1.index);
        const y1 = yAxis.convertToPixel(p1.value);
        const x2 = xAxis.convertToPixel(p2.index);
        const y2 = yAxis.convertToPixel(p2.value);
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
      }
      ctx.stroke();

      // ── MA line(s) (dashed, 1 px) ───────────────────────────────────────────
      for (const key of maKeys) {
        const maColor = maColorMap[key] ?? color;
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        let firstMA = true;
        for (let i = from; i <= to; i++) {
          const r = result[i];
          if (!r || r[key] == null || isNaN(r[key])) { firstMA = true; continue; }
          const x = xAxis.convertToPixel(i);
          const y = yAxis.convertToPixel(r[key]);
          if (firstMA) { ctx.moveTo(x, y); firstMA = false; }
          else { ctx.lineTo(x, y); }
        }
        ctx.strokeStyle = maColor;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // ── Last-value horizontal reference line ────────────────────────────────
      let lastValue: number | undefined;
      for (let i = to; i >= from; i--) {
        const v = result[i]?.value;
        if (v != null && !isNaN(v)) { lastValue = v; break; }
      }
      if (lastValue != null) {
        const y = yAxis.convertToPixel(lastValue);
        const rightX = bounding.width;
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.35;
        ctx.moveTo(0, y);
        ctx.lineTo(rightX, y);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.setLineDash([]);
      }

      ctx.restore();
      return true; // Suppress default KLineCharts figure rendering
    },
  };

  try {
    registerIndicator(template as never);
  } catch {
    // Already registered — update color via override (caller handles this)
  }
}
